<script setup lang="ts">
import { ref, watch, toRef, onScopeDispose, nextTick, useSession } from '#imports';
import { useI18n } from 'vue-i18n';
import { musicService } from '~/services/musicService';
import { useAuthState } from '~/composables/useAuthState';
import { useDialogFocus } from '~/composables/useDialogFocus';

const { t, locale } = useI18n();
const { isAuthReady, isAuthenticated, user, fetchAndStoreToken } = useAuthState();
const { session } = useSession();

// 定义积分历史记录的类型
interface PointsHistoryItem {
  id: string;
  reason: number; // 积分变动原因/类型
  count: number; // 积分数量
  reason_desc: string; // 描述
  created: number | string; // Existing API timestamp in milliseconds, or a date string.
}

// 使用props和emit代替defineModel
const props = defineProps<{
  visible: boolean
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>();

// 积分历史记录
const pointsHistory = ref<PointsHistoryItem[]>([]);
const isLoading = ref(false);
const hasMore = ref(true);
const currentPage = ref(1);
const pageSize = ref(10);
const loadError = ref('');
let historyRequest = 0;
let historyController: AbortController | null = null;
let failedPage = 1;
let failedAppend = false;

// 关闭弹窗
const closePopup = () => {
  resetHistory();
  emit('update:visible', false);
};
const { dialog, closeButton, handleKeydown } = useDialogFocus(toRef(props, 'visible'), closePopup);

// 格式化日期
const formatDate = (value: number | string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(locale.value);
};
const activityLabel = (item: PointsHistoryItem) => {
  if (typeof item.reason_desc === 'string' && item.reason_desc.trim()) return item.reason_desc;
  // These three codes are defined in music-server/pkg/repo/points_history.go.
  const known: Record<number, string> = { [-1]: 'music', [-9]: 'cover', [4]: 'returned' };
  return t('user.pointsActivity.' + (known[item.reason] || (item.reason > 0 ? 'added' : item.reason < 0 ? 'used' : 'other')));
};
function invalidateHistoryRequest() {
  ++historyRequest;
  historyController?.abort();
  historyController = null;
}
function resetHistory() {
  invalidateHistoryRequest();
  pointsHistory.value = [];
  currentPage.value = 1;
  hasMore.value = true;
  isLoading.value = false;
  loadError.value = '';
  failedPage = 1;
  failedAppend = false;
}

// 获取积分历史记录
const fetchPointsHistory = async (page = 1, append = false) => {
  if (isLoading.value) return;
  const owner = user.value?.id;
  const sessionId = session.value?.id;
  if (!props.visible || !isAuthReady.value || !isAuthenticated.value || !owner || !sessionId) return;
  const request = ++historyRequest;
  const controller = new AbortController();
  historyController = controller;
  const stillCurrent = () => request === historyRequest && props.visible && isAuthReady.value &&
    owner === user.value?.id && sessionId === session.value?.id && isAuthenticated.value;
  const recoverFocus = typeof document !== 'undefined' && dialog.value?.contains(document.activeElement);
  loadError.value = '';
  isLoading.value = true;
  try {
    if (!await fetchAndStoreToken()) throw new Error('Account unavailable');
    if (!stillCurrent()) return;
    const response = await musicService.getScoreHistory({
      page,
      size: pageSize.value
    }, { musicmintInlineError: true, signal: controller.signal });
    if (!stillCurrent()) return;
    if (response?.code !== 200 || !response.data || (!Array.isArray(response.data.list) && response.data.list !== null)) {
      throw new Error('History unavailable');
    }
    const items: PointsHistoryItem[] = response.data.list || [];
    if (items.some(item => !item || !item.id || !Number.isFinite(item.reason) || !Number.isFinite(item.count))) {
      throw new Error('History unavailable');
    }
    if (append) {
      const ids = new Set(pointsHistory.value.map(item => item.id));
      pointsHistory.value = [...pointsHistory.value, ...items.filter(item => !ids.has(item.id))];
    } else {
      pointsHistory.value = items;
    }
    // 判断是否还有更多数据
    const total = response.data.total;
    hasMore.value = items.length > 0 && (typeof total === 'number' && Number.isFinite(total) && total >= 0
      ? page * pageSize.value < total : items.length === pageSize.value);
    currentPage.value = page;
  } catch {
    if (stillCurrent()) {
      failedPage = page;
      failedAppend = append;
      loadError.value = t('common.load_failed');
    }
  } finally {
    if (historyController === controller) historyController = null;
    if (stillCurrent()) {
      isLoading.value = false;
      await nextTick();
      if (recoverFocus && stillCurrent() && document.activeElement === document.body) closeButton.value?.focus();
    }
  }
};
const retryHistory = () => fetchPointsHistory(failedPage, failedAppend);

// 加载更多
const loadMore = () => {
  if (hasMore.value && !isLoading.value && !loadError.value) {
    fetchPointsHistory(currentPage.value + 1, true);
  }
};

// 监听滚动事件，实现无限滚动
const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
  
  // 当滚动到距离底部50px时，加载更多数据
  if (scrollBottom < 50 && hasMore.value && !isLoading.value && !loadError.value) {
    loadMore();
  }
};

// 监听弹窗显示状态变化
watch([() => props.visible, isAuthReady, isAuthenticated, () => user.value?.id, () => session.value?.id], ([visible, ready, signedIn]) => {
  resetHistory();
  if (!visible) return;
  if (!ready) { isLoading.value = true; return; }
  if (!signedIn) { closePopup(); return; }
  void fetchPointsHistory(1, false);
}, { immediate: true, flush: 'sync' });

// Lock the app's actual scroll area; body alone does not own its scrolling.
let lockedPage: HTMLElement | null = null;
let pageOverflow = '';
let pageInert = false;
const releasePage = () => {
  if (!lockedPage) return;
  lockedPage.style.overflow = pageOverflow;
  lockedPage.inert = pageInert;
  lockedPage = null;
};
watch(() => props.visible, visible => {
  if (typeof document === 'undefined') return;
  releasePage();
  if (!visible) return;
  const page = document.querySelector<HTMLElement>('[data-musicmint-page-scroll]');
  if (!page) return;
  lockedPage = page;
  pageOverflow = page.style.overflow;
  pageInert = page.inert;
  page.style.overflow = 'hidden';
  page.inert = true;
}, { immediate: true, flush: 'post' });
onScopeDispose(() => { invalidateHistoryRequest(); releasePage(); });
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" 
         class="fixed inset-0 bg-black/50 p-4 backdrop-blur-sm z-[200] flex items-center justify-center"
         @click.self="closePopup" @keydown="handleKeydown">
      <div ref="dialog" role="dialog" aria-modal="true" :aria-label="t('user.pointsHistory')" tabindex="-1"
           class="relative flex flex-col w-full max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden"
           style="max-height: min(80dvh, calc(100dvh - 32px));">
        <!-- 弹窗头部 -->
        <div class="shrink-0 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center gap-3">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">
            {{ t('user.pointsHistory') }}
          </h3>
          <button ref="closeButton" type="button" :aria-label="t('common.close')" @click="closePopup" class="musicmint-dismiss">
            <span class="inline-block i-lucide-x h-4 w-4" aria-hidden="true"></span>
          </button>
        </div>
        
        <!-- 弹窗内容 -->
        <div class="min-h-0 px-4 sm:px-6 py-4 overflow-y-auto overscroll-contain" @scroll="handleScroll">
          <!-- 积分历史记录列表 -->
          <ol v-if="pointsHistory.length > 0" class="space-y-3">
            <li v-for="item in pointsHistory" :key="item.id" :data-points-id="item.id"
                 class="p-3 rounded-lg border border-gray-700/50">
              <div class="min-w-0">
                <div class="flex justify-between items-start gap-3">
                  <p class="min-w-0 flex-1 break-words text-sm font-medium text-gray-900 dark:text-white">{{ activityLabel(item) }}</p>
                  <div :class="[item.reason > 0 ? 'text-green-500' : 'text-red-500', 'shrink-0 whitespace-nowrap tabular-nums font-medium']">
                    {{ item.reason > 0 ? '+' : '-' }}{{ item.count }}
                  </div>
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-2 break-words">
                  {{ formatDate(item.created) }}
                </div>
              </div>
            </li>
          </ol>
          
          <!-- 加载中 -->
          <div v-if="isLoading" role="status" class="flex justify-center items-center py-4">
            <div class="i-lucide-loader-2 animate-spin text-blue-500 text-2xl"></div>
            <span class="ml-2 text-gray-600 dark:text-gray-300">{{ t('common.loading') }}</span>
          </div>
          
          <div v-else-if="loadError" role="alert" class="flex flex-col items-center gap-3 py-4 text-sm text-red-300">
            <p>{{ loadError }}</p>
            <button type="button" class="musicmint-secondary-button" @click="retryHistory">{{ t('common.retry') }}</button>
          </div>
          <button v-else-if="pointsHistory.length && hasMore" type="button" @click="loadMore" class="musicmint-secondary-button mt-4 w-full">{{ t('common.loadMore') }}</button>
          <p v-else-if="pointsHistory.length" class="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">{{ t('common.noMoreData') }}</p>
          <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
            {{ t('user.noPointsHistory') }}
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
